import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "@/components/ui/table";
import { Measurement } from "@/types/measurement";
import { MeasurementRequest } from "@/types/measurementRequest";
import { Download } from "lucide-react";
import { useMemo } from "react";
import {
  calculateProgressPercentage,
  formatDieLabel,
  formatProgressPercentage,
  formatTimeForDisplay,
  getGratingPriority,
  toCsvValue,
  formatDiagnosticValue,
} from "../MeasurementRequestDetails.functions";
import { DiagnosticRow } from "../MeasurementRequestDetails.types";
import { usePermissionContext } from "@/permissions";

const diagnosticColumns: Array<{ key: keyof Omit<DiagnosticRow, "id">; label: string }> = [
  { key: "time", label: "time" },
  { key: "tester", label: "tester" },
  { key: "progress", label: "progress" },
  { key: "lot", label: "lot" },
  { key: "wafer", label: "wafer" },
  { key: "mask", label: "mask" },
  { key: "dieRow", label: "die_row" },
  { key: "dieColumn", label: "die_column" },
  { key: "dieLabel", label: "die_label" },
  { key: "subDie", label: "sub_die" },
  { key: "testSite", label: "test_site" },
  { key: "device", label: "device" },
  { key: "portIndex", label: "port_index" },
  { key: "grating", label: "grating" },
  { key: "rotation", label: "rotation" },
  { key: "x", label: "x" },
  { key: "y", label: "y" },
  { key: "z", label: "z" },
  { key: "xNano", label: "x_nano" },
  { key: "yNano", label: "y_nano" },
  { key: "zNano", label: "z_nano" },
  { key: "h", label: "h" },
  { key: "overtravel", label: "overtravel" },
  { key: "alignedPower", label: "aligned_power" },
  { key: "alignSuccess", label: "align_success" },
  { key: "blindStep", label: "blind_step" },
];

const csvColumns: Array<{
  key: keyof Omit<DiagnosticRow, "id" | "time">;
  label: string;
}> = [
  { key: "experiment", label: "experiment" },
  { key: "rawTimestamp", label: "time" },
  { key: "progress", label: "progress" },
  { key: "tester", label: "tester" },
  { key: "lot", label: "lot" },
  { key: "wafer", label: "wafer" },
  { key: "mask", label: "mask" },
  { key: "dieRow", label: "die_row" },
  { key: "dieColumn", label: "die_column" },
  { key: "dieLabel", label: "die_label" },
  { key: "subDie", label: "sub_die" },
  { key: "testSite", label: "test_site" },
  { key: "device", label: "device" },
  { key: "portIndex", label: "port_index" },
  { key: "grating", label: "grating" },
  { key: "rotation", label: "rotation" },
  { key: "x", label: "x" },
  { key: "y", label: "y" },
  { key: "z", label: "z" },
  { key: "xNano", label: "x_nano" },
  { key: "yNano", label: "y_nano" },
  { key: "zNano", label: "z_nano" },
  { key: "h", label: "h" },
  { key: "overtravel", label: "overtravel" },
  { key: "alignedPower", label: "aligned_power" },
  { key: "alignSuccess", label: "align_success" },
  { key: "blindStep", label: "blind_step" },
];

export function MeasurementDiagnostics({
  measurementRequest,
  measurements,
}: {
  measurementRequest: MeasurementRequest;
  measurements: Measurement[];
}) {
  const { hasPermission } = usePermissionContext();
  const canReadMeasurementDiagnostics = hasPermission("measurement-diagnostics", "read");

  const highestProgressMeasurement = useMemo(() => {
    return measurements.reduce<{
      step: number | null;
      maxStep: number | null;
      progressPercentage: number | null;
    } | null>((highestMeasurement, measurement) => {
      const highestStep = measurement.outputs.reduce(
        (max, output) => Math.max(max, output.step),
        0,
      );
      const progressPercentage = calculateProgressPercentage(highestStep, measurement.maxStep);

      if (progressPercentage === null) {
        return highestMeasurement;
      }

      if (
        highestMeasurement === null ||
        highestMeasurement.progressPercentage === null ||
        progressPercentage > highestMeasurement.progressPercentage
      ) {
        return {
          step: highestStep,
          maxStep: measurement.maxStep,
          progressPercentage,
        };
      }

      return highestMeasurement;
    }, null);
  }, [measurements]);

  const rows = useMemo<DiagnosticRow[]>(() => {
    const lot = measurementRequest.imecLotId ?? measurementRequest.sfabLotId ?? "---";

    const unsortedRows = measurements.flatMap((measurement) => {
      return measurement.outputs.map((output) => ({
        id: `${measurement.id}-${output.id}`,
        experiment: measurement.experiment ?? measurementRequest.name,
        rawTimestamp: measurement.timestamp.toISOString(),
        progress: output.step > 0 ? `${output.step}/${measurement.maxStep}` : "-",
        time: formatTimeForDisplay(measurement.timestamp),
        tester: measurement.waferRequest?.measurementSystem?.name ?? "---",
        lot,
        wafer: measurement.waferRequest?.wafer.name ?? "---",
        mask: measurementRequest.mask.name,
        dieRow: output.dieRow,
        dieColumn: output.dieColumn,
        dieLabel: formatDieLabel(output.dieRow, output.dieColumn),
        subDie: measurement.portCombo.structure.subDieName,
        testSite: measurement.portCombo.structure.testSiteName,
        device: measurement.portCombo.structure.name,
        portIndex: measurement.portCombo.index,
        grating: output.opticalPort.name,
        rotation: output.rotation,
        x: output.x,
        y: output.y,
        z: output.z,
        xNano: output.xNano,
        yNano: output.yNano,
        zNano: output.zNano,
        h: output.h,
        overtravel: output.overtravel,
        alignedPower: output.alignedPower,
        alignSuccess: output.alignSuccess,
        blindStep: measurement.portCombo.blindStep,
      }));
    });

    return unsortedRows.sort((a, b) => {
      const stepComparison =
        (a.progress ? parseInt(a.progress.split("/")[0], 10) : 0) -
        (b.progress ? parseInt(b.progress.split("/")[0], 10) : 0);
      if (stepComparison !== 0) {
        return stepComparison;
      }
      const waferComparison = a.wafer.localeCompare(b.wafer, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      if (waferComparison !== 0) {
        return waferComparison;
      }

      const portIndexComparison = a.portIndex - b.portIndex;
      if (portIndexComparison !== 0) {
        return portIndexComparison;
      }

      const gratingPriorityComparison =
        getGratingPriority(a.grating) - getGratingPriority(b.grating);
      if (gratingPriorityComparison !== 0) {
        return gratingPriorityComparison;
      }

      return a.grating.localeCompare(b.grating, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [measurementRequest, measurements]);

  const onDownloadCsv = () => {
    const headers = csvColumns.map((column) => column.label).join(",");
    const body = rows
      .map((row) => csvColumns.map((column) => toCsvValue(row[column.key])).join(","))
      .join("\n");

    const csvContent = `${headers}\n${body}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `measurement_request_${measurementRequest.id}_diagnostics.csv`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  };

  if (!canReadMeasurementDiagnostics) {
    return null;
  }

  if (rows.length === 0) {
    return (
      <h3 className="text-lg font-semibold">
        No diagnostics available for this measurement request
      </h3>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Diagnostics</CardTitle>
        <Button type="button" variant="outline" size="sm" onClick={onDownloadCsv}>
          <Download className="h-4 w-4 mr-2" />
          Download CSV
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-6 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Progress</span>
            <span>
              {highestProgressMeasurement === null
                ? "---"
                : `[${highestProgressMeasurement.step} / ${highestProgressMeasurement.maxStep}] (${formatProgressPercentage(highestProgressMeasurement.progressPercentage)}%)`}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{
                width: `${Math.max(
                  0,
                  Math.min(100, highestProgressMeasurement?.progressPercentage ?? 0),
                ).toFixed(1)}%`,
              }}
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              {diagnosticColumns.map((column) => (
                <TableHead key={column.key}>{column.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                {diagnosticColumns.map((column) => (
                  <TableCell key={column.key}>{formatDiagnosticValue(row[column.key])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
